export type TextStyle = Record<string, unknown>;

export type OutputBufferJob =
    | { kind: "line"; text: string; style?: TextStyle; instant?: boolean }
    | { kind: "run"; fn: () => void };

export interface TeletypeAnimationConfig {
    wordDelayMs: number;
    getDelay?: (token: string, index: number, totalTokens: number) => number;
}

export interface LineHandle {
    setText: (text: string) => void;
    getHeight: () => number;
}

export interface OutputBufferHost {
    wrapLine: (text: string, style?: TextStyle) => string[];
    createLine: (y: number, style?: TextStyle) => LineHandle;
    advanceLine: (lineY: number, lineHeight: number) => void;
    onScroll: () => void;
    schedule: (delayMs: number, fn: () => void) => void;
}

export const DEFAULT_TELETYPE_ANIMATION: TeletypeAnimationConfig = {
    wordDelayMs: 32,
};

export function wordDelayAnimation(wordDelayMs: number): TeletypeAnimationConfig {
    return { wordDelayMs };
}

export function variableDelayAnimation(
    getDelay: (token: string, index: number, totalTokens: number) => number,
    fallbackMs = DEFAULT_TELETYPE_ANIMATION.wordDelayMs,
): TeletypeAnimationConfig {
    return { wordDelayMs: fallbackMs, getDelay };
}

function tokenize(text: string): string[] {
    return text.match(/\S+|\s+/g) ?? [];
}

export class OutputBuffer {
    private queue: OutputBufferJob[] = [];
    private busy = false;
    private generation = 0;

    constructor(
        private readonly host: OutputBufferHost,
        private animation: TeletypeAnimationConfig,
        private readonly getLineY: () => number,
    ) {}

    setAnimation(animation: TeletypeAnimationConfig): void {
        this.animation = animation;
    }

    reset(): void {
        this.generation += 1;
        this.queue = [];
        this.busy = false;
    }

    feed(job: OutputBufferJob): void {
        this.queue.push(job);
        this.drain();
    }

    feedLine(text: string, style?: TextStyle, instant = false): void {
        this.feed({ kind: "line", text, style, instant });
    }

    feedLines(texts: string[], style?: TextStyle, instant = false): void {
        for (const text of texts) {
            this.feedLine(text, style, instant);
        }
    }

    feedRun(fn: () => void): void {
        this.feed({ kind: "run", fn });
    }

    feedWrapped(text: string, style?: TextStyle, instant = false): void {
        for (const line of this.host.wrapLine(text, style)) {
            this.feedLine(line, style, instant);
        }
    }

    private drain(): void {
        if (this.busy) return;

        const job = this.queue.shift();
        if (!job) return;

        this.busy = true;

        if (job.kind === "run") {
            job.fn();
            this.busy = false;
            this.drain();
            return;
        }

        if (job.instant || this.animation.wordDelayMs <= 0) {
            this.paintInstant(job.text, job.style ?? {}, () => {
                this.busy = false;
                this.drain();
            });
            return;
        }

        this.playLine(job.text, job.style ?? {}, () => {
            this.busy = false;
            this.drain();
        });
    }

    private paintInstant(text: string, style: TextStyle, onComplete: () => void): void {
        const lineY = this.getLineY();
        const line = this.host.createLine(lineY, style);
        line.setText(text);
        this.host.advanceLine(lineY, line.getHeight());
        this.host.onScroll();
        onComplete();
    }

    private playLine(text: string, style: TextStyle, onComplete: () => void): void {
        const generation = this.generation;
        const tokens = tokenize(text);

        if (tokens.length === 0) {
            this.paintInstant("", style, onComplete);
            return;
        }

        const lineY = this.getLineY();
        const line = this.host.createLine(lineY, style);

        let index = 0;
        let revealed = "";

        const revealNext = (): void => {
            if (generation !== this.generation) {
                onComplete();
                return;
            }

            if (index >= tokens.length) {
                this.host.advanceLine(lineY, line.getHeight());
                this.host.onScroll();
                onComplete();
                return;
            }

            const token = tokens[index]!;
            revealed += token;
            index += 1;
            line.setText(revealed);
            this.host.onScroll();

            const delay =
                this.animation.getDelay?.(token, index - 1, tokens.length) ??
                this.animation.wordDelayMs;

            this.host.schedule(delay, revealNext);
        };

        revealNext();
    }
}
